import { useEffect, useMemo, Fragment, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Col, Row, Form, Input, Select, Button, Spin, Popover, Divider } from 'antd';
import { orderBy } from 'lodash';
import Diagnostics from '../../../components/Diagnostics';
import useRecommendationsModal from '../../../components/hooks/useRecommendationsModal';
import ConclusionsModal from '../../../MedicalConsultationPage/components/ConclusionsModal';
import ECGInterpreter from '../ECGInterpreter';
import { filterAndSortExpertFields } from './signatureUtils';
import { EXAM_SPECIALTY_IDS } from '../../../MedicalConsultationPage/constants/examSpecialties';

const { TextArea } = Input;
const { Option } = Select;

const buildExpertFieldsForSave = (allExpertFields = [], visibleExpertFields, formValues) => {
  const visibleFieldIds = new Set(visibleExpertFields.map((field) => field.id));
  return allExpertFields.map((field) => {
    if (!visibleFieldIds.has(field.id)) {
      return { ...field, result: field.input_type === 'diagnostic' ? [] : '' };
    }
    const formValue = formValues[field.id];
    const hasValue = formValue && formValue !== '';
    return { ...field, result: hasValue ? formValue : field.result };
  });
};
const SignatureInputs = forwardRef(({ id, loading, formData, updateExamSignature, examData, countryCode, patientFullName, countrySettings, t, isDrawer, autoFillDefaults, onSubmit, onGlobalSubmit, isAnyLoading, isAnySigned }, ref) => {
  const [form] = Form.useForm();
  const isPsychology = formData?.code === 'CONSULTA_PSICO';
  
  const ecgUrl = useMemo(() => {
    const ecgFile = examData?.procedures?.find(p => p.code === 'IMAGE_EKG' && p.input_type === 'file')
      || examData?.measurements?.find(m => m.code === 'IMAGE_EKG' && m.input_type === 'file');
    return ecgFile?.file_data?.document_url;
  }, [examData]);

  const recommendatiosModal = useRecommendationsModal({
    t,
    patientFullName,
    service: {
      id: examData.id,
      type: 'exam',
      form,
    },
    countryCode,
    medicalForm: form,
    countrySettings,
  });

  const allValues = Form.useWatch([], form);

  const conditionValues = useMemo(() => {
    if (!examData?.extra_data_exam?.expertFieldGroups) return {};
    const keys = [...new Set(examData.extra_data_exam.expertFieldGroups.flatMap((group) => Object.keys(group.conditions || {})))];
    return keys.reduce((acc, key) => {
      acc[key] = allValues?.[key];
      return acc;
    }, {});
  }, [allValues, examData?.extra_data_exam?.expertFieldGroups]);

  const sortedInputs = useMemo(() => {
    return filterAndSortExpertFields({
      expertFields: formData.expert_fields,
      expertFieldGroups: examData?.extra_data_exam?.expertFieldGroups,
      getFieldValue: (id) => form.getFieldValue(id),
      isDrawer
    });
  }, [examData?.extra_data_exam?.expertFieldGroups, conditionValues, formData.expert_fields, isDrawer]);

  const createField = (inputData) => {
    const fields = {
      text: <TextArea id="textAreaSignature" className="cmt-input signature-textarea" placeholder={`Ingrese ${inputData.name.toLowerCase()}`} />,
      select: (
        <Select id="selectSignature" className="cmt-select" placeholder={`Seleccione ${inputData.name.toLowerCase()}`}>
          {inputData.options &&
            inputData.options.length > 0 &&
            inputData.options.map((option, inx) => {
              return (
                <Option key={inx} value={option}>
                  {option}
                </Option>
              );
            })}
        </Select>
      ),
      diagnostic: (
        <Col lg={24} xs={24}>
          <Row align="middle">
            <Col className="mr-10" span={24}>
              <strong>{inputData.name}</strong>
            </Col>
            <Col className="mr-10 mt-10" span={24}>
              <Diagnostics form={form} formData={formData} recommendatiosModal={recommendatiosModal} inputData={inputData} />
            </Col>
          </Row>
        </Col>
      ),
    };

    return fields[inputData.input_type];
  };

  const handleFormButtons = (signal) => {
    form.setFieldsValue({ saved: signal === 'saved' });
    form.setFieldsValue({ readyToSign: signal === 'readyToSign' });
    form.setFieldsValue({ signed: signal === 'signed' });
  };

  const buildExamPayload = useCallback(() => {
    const values = form.getFieldsValue();
    return {
      admissionPatientExamId: id,
      body: {
        diagnostics: values['diagnostics']?.filter((diagnostic) => diagnostic?.id),
        expertFields: sortedInputs.map((input) => ({
          ...input,
          result: values[input.id] && values[input.id] !== '' ? values[input.id] : null,
        })),
      },
    };
  }, [form, id, sortedInputs]);

  useImperativeHandle(ref, () => ({
    validateAndGetPayload: async ({ sign }) => {
      form.setFieldsValue({ signed: sign });
      try {
        await form.validateFields();
      } catch (error) {
        form.setFieldsValue({ signed: false });
        throw error;
      }
      return buildExamPayload();
    }
  }));

  const handleStatusDetected = useCallback((status, inputData) => {
    const currentValue = form.getFieldValue(inputData.id);
    if (!currentValue) {
      const matchingOption = inputData.options?.find(
        (opt) => opt.toLowerCase() === status.toLowerCase()
      );
      if (matchingOption) {
        form.setFieldsValue({ [inputData.id]: matchingOption });
      }
    }
  }, [form]);

  const onFinish = (values) => {
    let type;
    const finalExamData = {
      admissionPatientExamId: id,
      body: {
        diagnostics: values['diagnostics']?.filter((diagnostic) => diagnostic?.id),
      },
    };

    if (!values['signed']) {
      const expertFieldsWithClearedHiddenDiagnostics = buildExpertFieldsForSave(formData.expert_fields || [], sortedInputs, values);
      finalExamData.body.expertFields = expertFieldsWithClearedHiddenDiagnostics;
      type = values['saved'] ? 'saved' : 'readyToSign';
    } else {
      let results = [...sortedInputs];

      results.forEach((input, index) => {
        if (values[input.id]) {
          const value = values[input.id];
          const checkValue = value && value !== '' ? value : null;

          results[index].result = checkValue;
        }
      });

      finalExamData.body.expertFields = results;
      type = 'signed';
    }
    updateExamSignature(finalExamData, type);
  };

  useEffect(() => {
    if (formData) {
      form.setFieldsValue({ diagnostics: formData.diagnostics });
      formData?.expert_fields?.forEach((field) => {
        if (field.input_type === 'diagnostic') {
          form.setFieldsValue({ [field.id]: field.result || [] });
        }
      });
    }
  }, [formData]);

  useEffect(() => {
    if (autoFillDefaults && Object.keys(autoFillDefaults).length > 0) {
      Object.entries(autoFillDefaults).forEach(([fieldId, value]) => {
        const currentValue = form.getFieldValue(fieldId);
        if (!currentValue) {
          form.setFieldsValue({ [fieldId]: value });
        }
      });
    }
  }, [autoFillDefaults, form]);

  return (
    <Spin className="cmt-spin" spinning={loading.form}>
      <Row justify="center" gutter={[0, 20]}>
        {formData?.id && (
          <>
            <Col lg={24} xs={24}>
              {isDrawer ? (
                <Divider className="exam-detail-drawer__separator" />
              ) : (
                <h2>{formData.name}</h2>
              )}
            </Col>
            <Col lg={24} xs={24}>
              <Form form={form} onFinish={onFinish} layout="vertical" size="large">
                <Row gutter={[0, 20]}>
                  {sortedInputs.length > 0 &&
                    (() => {
                      const renderedGroups = new Set();
                      return sortedInputs.map((inputData, i) => {
                        const group = examData?.extra_data_exam?.expertFieldGroups?.find((group) => group.codes.includes(inputData.code));

                        const showGroupTitle = group && !renderedGroups.has(group.title);

                        if (showGroupTitle) renderedGroups.add(group.title);

                        return (
                          <Fragment key={inputData.id}>
                            {showGroupTitle && (
                              <Col lg={24} xs={24}>
                                <h3 className="mt-40 mb-0">{group.title}</h3>
                              </Col>
                            )}
                            {inputData.input_type === 'diagnostic' ? (
                              createField(inputData)
                            ) : (
                              <Col lg={24} xs={24}>
                                <Form.Item
                                  name={inputData.id}
                                  initialValue={inputData.result || autoFillDefaults?.[inputData.id] || inputData.default_value}
                                  label={
                                    <Row align="middle">
                                      <Col className="mr-10">
                                        <strong>{inputData.name}</strong>
                                      </Col>
                                    </Row>
                                  }
                                  rules={[
                                    ({ getFieldValue }) => ({
                                      validator(_, value) {
                                        if ((getFieldValue('readyToSign') || getFieldValue('signed')) && (!value || value === '')) {
                                          return Promise.reject(new Error('Complete el campo'));
                                        } else {
                                          return Promise.resolve();
                                        }
                                      },
                                    }),
                                  ]}
                                >
                                  {createField(inputData)}
                                </Form.Item>
                                { /* inputData.code === 'ESTADO_1_EKG' && ecgUrl && (
                                  <div className="mb-20">
                                    <ECGInterpreter
                                      source={ecgUrl}
                                      getUrl={(url) => url}
                                      tooltip="Analizar ECG con IA"
                                      onStatusDetected={(status) => handleStatusDetected(status, inputData)}
                                    />
                                  </div>
                                ) */}
                              </Col>
                            )}
                          </Fragment>
                        );
                      });
                    })()}

                  {!isPsychology && !(formData.expert_fields || []).some((input) => input.input_type === 'diagnostic')
                  && examData?.exam_specialty_id !== EXAM_SPECIALTY_IDS[countryCode]?.ULTRASOUND
                  && examData?.form_type !== 'interconsultation' && (
                    <Col lg={24} xs={24}>
                      <Row align="middle">
                        <Col className="mr-10" span={24}>
                          <strong>{`Diagnósticos ${examData?.name}`}</strong>
                        </Col>
                        <Col className="mr-10 mt-10" span={24}>
                          <Diagnostics form={form} formData={formData} recommendatiosModal={recommendatiosModal} />
                        </Col>
                      </Row>
                    </Col>
                  )}
                </Row>
                {isDrawer ? (() => {
                  return (
                    <Row justify="end" className="mt-20" gutter={[12, 0]}>
                      <Col>
                        <Button
                          id="saveExpertFieldsForm"
                          loading={loading.saved || isAnyLoading}
                          disabled={loading.saved || isAnyLoading}
                          className={
                            loading.saved || isAnyLoading
                              ? 'cmt-button disabled'
                              : 'cmt-button primary'
                          }
                          type="primary"
                          onClick={() => {
                            if (onGlobalSubmit) onGlobalSubmit({ sign: false });
                            else onSubmit(buildExamPayload());
                          }}
                        >
                          Guardar
                        </Button>
                      </Col>
                      <Col>
                        <Popover
                          content={isAnySigned ? 'Hay exámenes ya firmados. Presione Guardar primero para sincronizar estados.' : null}
                          trigger={isAnySigned ? 'hover' : 'none'}
                        >
                          <span>
                            <Button
                              id="saveAndSignForm"
                              loading={loading.saved || isAnyLoading}
                              disabled={loading.saved || isAnyLoading || isAnySigned || formData?.status === 'signed'}
                              className={
                                loading.saved || isAnyLoading || isAnySigned || formData?.status === 'signed'
                                  ? 'cmt-button disabled'
                                  : 'cmt-button tertiary'
                              }
                              type="primary"
                              style={isAnySigned || formData?.status === 'signed' ? { pointerEvents: 'none' } : {}}
                              onClick={async () => {
                                if (onGlobalSubmit) {
                                  onGlobalSubmit({ sign: true });
                                } else {
                                  form.setFieldsValue({ signed: true });
                                  try {
                                    await form.validateFields();
                                  } catch {
                                    form.setFieldsValue({ signed: false });
                                    return;
                                  }
                                  onSubmit(buildExamPayload(), { sign: true });
                                }
                              }}
                            >
                              Guardar y firmar
                            </Button>
                          </span>
                        </Popover>
                      </Col>
                    </Row>
                  );
                })() : (
                <Row gutter={20} className="mt-60">
                  <Col lg={6} xs={24}>
                    <Form.Item name="saved">
                      <Button
                        id="saveSignatureForm"
                        loading={loading.saved}
                        disabled={loading.saved}
                        className={loading.saved ? 'cmt-button disabled' : 'cmt-button primary'}
                        type="primary"
                        htmlType="submit"
                        onClick={() => handleFormButtons('saved')}
                      >
                        Guardar en borrador
                      </Button>
                    </Form.Item>
                  </Col>
                  <Col lg={6} xs={24}>
                    <Form.Item name="readyToSign">
                      <Button
                        id="readyToSignSignatureForm"
                        loading={loading.saved}
                        disabled={loading.saved}
                        className={loading.saved ? 'cmt-button disabled outline' : 'cmt-button primary outline'}
                        type="primary"
                        htmlType="submit"
                        onClick={() => handleFormButtons('readyToSign')}
                      >
                        Guardar y finalizar
                      </Button>
                    </Form.Item>
                  </Col>
                  {(formData.status === 'readyToSign' || formData.status === 'signed') && (
                    <Col lg={6} xs={24}>
                      <Form.Item name="signed">
                        <Popover
                          title="Exámen pendiente"
                          trigger={examData.state !== 'done' ? 'hover' : 'none'}
                          content={
                            <div>
                              <span>Examen no se encuentra como finalizado</span>
                            </div>
                          }
                          overlayClassName="sidebar-tooltip"
                        >
                          <Button
                            id="signSignatureForm"
                            loading={loading.saved}
                            disabled={loading.saved || formData.status === 'signed' || examData.state !== 'done'}
                            className={
                              loading.saved || formData.status === 'signed' || examData.state !== 'done'
                                ? 'cmt-button disabled'
                                : 'cmt-button tertiary'
                            }
                            type="primary"
                            htmlType="submit"
                            onClick={() => handleFormButtons('signed')}
                          >
                            Firmar
                          </Button>
                        </Popover>
                      </Form.Item>
                    </Col>
                  )}
                </Row>
                )}
              </Form>
            </Col>
          </>
        )}
      </Row>
      <ConclusionsModal recommendatiosModal={recommendatiosModal} />
    </Spin>
  );
});

export default SignatureInputs;
